import { useEffect, useMemo } from "react";

import { useForm } from "@mantine/form";
import { errorModal, successModal } from "modules/error/ErrorHandlingModals";
import {
  TUserFeatureFlagModalForm,
  useGetFeatureFlagsQuery,
  usePutFeatureFlagMutation,
  UserInterface
} from "modules/users";
import { TFeatureFlag } from "types";
import { mergeFeaturesFlags } from "utils";

import { useGetFeatureFlagsAccountUserQuery } from "../../hooks/useGetFeatureFlagsAccountQuery";
import {
  UserFeatureFlagsModalContainer,
  UserFeatureFlagsModalDescription,
  UserFeatureFlagsModalHeader
} from "../styles";
import { UserFeatureFlagsModalBody } from "./UserFeatureFlagsModalBody";
import { UserFeatureFlagsModalFooter } from "./UserFeatureFlagsModalFooter";
import { userFeatureFlagsFormInitial } from "./userFeatureFlagsModal.data";

type UserFeatureFlagsModalProps = {
  close: () => void;
  opened: boolean;
  user?: UserInterface;
};

export const UserFeatureFlagsModal = ({ opened, close, user }: UserFeatureFlagsModalProps) => {
  const { id: userId = "", firstName = "", lastName = "" } = user || {};

  const form = useForm<TUserFeatureFlagModalForm>({
    initialValues: userFeatureFlagsFormInitial
  });

  const { getInputProps, onSubmit, setFieldValue, reset } = form;

  const accountId = getInputProps("accountId").value;

  const updatedFeatures = getInputProps("features").value as TFeatureFlag[];

  const { data: featureFlags, isSuccess: featureFlagsSuccess } = useGetFeatureFlagsQuery();

  const { data: accountFeatureFlags, isSuccess: accountFeatureFlagsSuccess } = useGetFeatureFlagsAccountUserQuery({
    accountId,
    userId
  });

  const features = useMemo(() => {
    if (!accountFeatureFlagsSuccess || !featureFlagsSuccess) return [];

    const base = featureFlags.data;

    const extension = accountFeatureFlags.data;

    return mergeFeaturesFlags(base, extension);
  }, [accountFeatureFlags?.data]);

  const onSuccess = () => {
    close();
    successModal("Feature Flags are sucessfully updated");
  };

  const onError = () => {
    close();
    errorModal("Feature Flags could not be updated right now. Please try again later.");
  };

  const { mutate } = usePutFeatureFlagMutation({ onSuccess, onError });

  const submit = () => mutate({ accountId, userId, features: updatedFeatures, previousData: features });

  useEffect(() => setFieldValue("features", features), [features]);

  useEffect(() => {
    if (!opened) reset();
  }, [opened]);

  return (
    <UserFeatureFlagsModalContainer
      opened={opened}
      handleClose={close}
      withCloseButton
      title={
        <div>
          <UserFeatureFlagsModalHeader>Feature Flags</UserFeatureFlagsModalHeader>
          <UserFeatureFlagsModalDescription>
            Select Features to be turned on for{" "}
            <i>
              {firstName} {lastName}
            </i>
          </UserFeatureFlagsModalDescription>
        </div>
      }
    >
      <form onSubmit={onSubmit(submit)}>
        <UserFeatureFlagsModalBody formInstance={form} userId={userId} />
        <UserFeatureFlagsModalFooter close={close} />
      </form>
    </UserFeatureFlagsModalContainer>
  );
};
